// ============================================================================
// NETWORK MANAGER - Orchestrates P2P Connections
// ============================================================================

import { EventEmitter } from './EventEmitter';
import { PeerConnection } from './PeerConnection';
import { SignalingClient } from './SignalingClient';
import { FirestoreSignaling } from './FirestoreSignaling';
import { PacketSerializer } from './PacketSerializer';
import { Packet, PacketType } from '@/shared/types';

export interface NetworkEvents {
  connected: void;
  disconnected: void;
  roomCreated: { roomCode: string };
  roomJoined: { roomCode: string };
  peerConnected: { peerId: string };
  peerDisconnected: { peerId: string };
  packet: { peerId: string; packet: Packet };
  error: { message: string };
}

export interface NetworkConfig {
  signalingUrl?: string;
  firebaseConfig?: Record<string, string>;
  useFirestore?: boolean;
  isHost: boolean;
}

export class NetworkManager extends EventEmitter<NetworkEvents> {
  private signaling: SignalingClient | null = null;
  private firestoreSignaling: FirestoreSignaling | null = null;
  private peers = new Map<string, PeerConnection>();
  private serializer = new PacketSerializer();
  
  private isHost: boolean;
  private localPeerId = '';
  private roomCode = '';
  private useFirestore: boolean;

  // Ping tracking
  private pingTimes = new Map<string, number>();
  private latencies = new Map<string, number>();

  constructor(config: NetworkConfig) {
    super();
    this.isHost = config.isHost;
    this.useFirestore = config.useFirestore ?? false;
  }

  // Connect using WebSocket signaling server
  async connect(signalingUrl: string): Promise<void> {
    this.signaling = new SignalingClient(signalingUrl);
    this.setupSignalingListeners();
    await this.signaling.connect();
    this.localPeerId = this.signaling.getLocalPeerId();
    this.emit('connected', undefined);
  }

  // Connect using Firebase Firestore (no server needed!)
  async connectWithFirebase(firebaseConfig: Record<string, string>): Promise<void> {
    this.useFirestore = true;
    this.firestoreSignaling = new FirestoreSignaling();
    this.setupFirestoreListeners();
    await this.firestoreSignaling.connect(firebaseConfig);
    this.localPeerId = this.firestoreSignaling.getLocalPeerId();
    this.emit('connected', undefined);
  }

  private setupSignalingListeners(): void {
    if (!this.signaling) return;

    this.signaling.on('roomCreated', ({ roomCode }) => {
      this.roomCode = roomCode;
      console.log(`[Network] Room created: ${roomCode}`);
      this.emit('roomCreated', { roomCode });
    });

    this.signaling.on('roomJoined', ({ roomCode, peers }) => {
      this.roomCode = roomCode;
      console.log(`[Network] Joined room: ${roomCode}, peers:`, peers);
      this.emit('roomJoined', { roomCode });

      // Initiate connections to existing peers
      for (const peerId of peers) {
        this.initiateConnection(peerId);
      }
    });

    this.signaling.on('peerJoined', ({ peerId }) => {
      console.log(`[Network] Peer joined: ${peerId}`);
      // New peer will initiate connection to us
    });

    this.signaling.on('peerLeft', ({ peerId }) => {
      console.log(`[Network] Peer left: ${peerId}`);
      this.removePeer(peerId);
    });

    this.signaling.on('offer', async ({ peerId, offer }) => {
      console.log(`[Network] Received offer from: ${peerId}`);
      await this.handleOffer(peerId, offer);
    });

    this.signaling.on('answer', async ({ peerId, answer }) => {
      console.log(`[Network] Received answer from: ${peerId}`);
      const peer = this.peers.get(peerId);
      if (peer) {
        await peer.handleAnswer(answer);
      }
    });

    this.signaling.on('iceCandidate', async ({ peerId, candidate }) => {
      const peer = this.peers.get(peerId);
      if (peer) {
        await peer.addIceCandidate(candidate);
      }
    });

    this.signaling.on('error', ({ message }) => {
      console.error(`[Network] Signaling error: ${message}`);
      this.emit('error', { message });
    });

    this.signaling.on('disconnected', () => {
      console.log('[Network] Signaling disconnected');
      this.emit('disconnected', undefined);
    });
  }

  private setupFirestoreListeners(): void {
    if (!this.firestoreSignaling) return;

    this.firestoreSignaling.on('roomCreated', ({ roomCode }) => {
      this.roomCode = roomCode;
      console.log(`[Network] Room created (Firestore): ${roomCode}`);
      this.emit('roomCreated', { roomCode });
    });

    this.firestoreSignaling.on('roomJoined', ({ roomCode, peers }) => {
      this.roomCode = roomCode;
      console.log(`[Network] Joined room (Firestore): ${roomCode}`);
      console.log(`[Network] Existing peers in room: [${peers.join(', ')}]`);
      this.emit('roomJoined', { roomCode });

      // Initiate connections to existing peers
      console.log(`[Network] Will initiate connections to ${peers.length} peer(s)`);
      for (const peerId of peers) {
        console.log(`[Network] Initiating connection to peer: ${peerId}`);
        this.initiateConnectionFirestore(peerId);
      }
    });

    this.firestoreSignaling.on('peerJoined', ({ peerId }) => {
      console.log(`[Network] Peer joined (Firestore): ${peerId}`);
      // New peer will initiate connection to us
    });

    this.firestoreSignaling.on('peerLeft', ({ peerId }) => {
      console.log(`[Network] Peer left (Firestore): ${peerId}`);
      this.removePeer(peerId);
    });

    this.firestoreSignaling.on('offer', async ({ peerId, offer }) => {
      console.log(`[Network] Received offer (Firestore) from: ${peerId}`);
      await this.handleOfferFirestore(peerId, offer);
    });

    this.firestoreSignaling.on('answer', async ({ peerId, answer }) => {
      console.log(`[Network] Received answer (Firestore) from: ${peerId}`);
      const peer = this.peers.get(peerId);
      if (peer) {
        await peer.handleAnswer(answer);
      }
    });

    this.firestoreSignaling.on('iceCandidate', async ({ peerId, candidate }) => {
      const peer = this.peers.get(peerId);
      if (peer) {
        await peer.addIceCandidate(candidate);
      }
    });

    this.firestoreSignaling.on('error', ({ message }) => {
      console.error(`[Network] Firestore signaling error: ${message}`);
      this.emit('error', { message });
    });

    this.firestoreSignaling.on('disconnected', () => {
      console.log('[Network] Firestore signaling disconnected');
      this.emit('disconnected', undefined);
    });
  }

  // WebSocket signaling connection methods
  private async initiateConnection(peerId: string): Promise<void> {
    const peer = new PeerConnection(peerId);
    this.setupPeerListeners(peer);
    this.peers.set(peerId, peer);

    peer.onIceCandidate((candidate) => {
      this.signaling?.sendIceCandidate(peerId, candidate.toJSON());
    });

    const offer = await peer.createOffer();
    this.signaling?.sendOffer(peerId, offer);
  }

  private async handleOffer(peerId: string, offer: RTCSessionDescriptionInit): Promise<void> {
    const peer = new PeerConnection(peerId);
    this.setupPeerListeners(peer);
    this.peers.set(peerId, peer);

    peer.onIceCandidate((candidate) => {
      this.signaling?.sendIceCandidate(peerId, candidate.toJSON());
    });

    const answer = await peer.handleOffer(offer);
    this.signaling?.sendAnswer(peerId, answer);
  }

  // Firestore signaling connection methods
  private async initiateConnectionFirestore(peerId: string): Promise<void> {
    console.log(`[Network] Initiating WebRTC connection to: ${peerId}`);
    const peer = new PeerConnection(peerId);
    this.setupPeerListeners(peer);
    this.peers.set(peerId, peer);
    console.log(`[Network] Peer added to map. Total peers: ${this.peers.size}`);

    peer.onIceCandidate((candidate) => {
      console.log(`[Network] Sending ICE candidate to: ${peerId}`);
      this.firestoreSignaling?.sendIceCandidate(peerId, candidate.toJSON());
    });

    try {
      const offer = await peer.createOffer();
      console.log(`[Network] Created offer for: ${peerId}`);
      await this.firestoreSignaling?.sendOffer(peerId, offer);
      console.log(`[Network] Sent offer to: ${peerId}`);
    } catch (e) {
      console.error(`[Network] Failed to create/send offer to ${peerId}:`, e);
    }
  }

  private async handleOfferFirestore(peerId: string, offer: RTCSessionDescriptionInit): Promise<void> {
    console.log(`[Network] Handling offer from: ${peerId}`);
    const peer = new PeerConnection(peerId);
    this.setupPeerListeners(peer);
    this.peers.set(peerId, peer);
    console.log(`[Network] Peer added to map from offer. Total peers: ${this.peers.size}`);

    peer.onIceCandidate((candidate) => {
      console.log(`[Network] Sending ICE candidate to: ${peerId}`);
      this.firestoreSignaling?.sendIceCandidate(peerId, candidate.toJSON());
    });

    try {
      const answer = await peer.handleOffer(offer);
      console.log(`[Network] Created answer for: ${peerId}`);
      await this.firestoreSignaling?.sendAnswer(peerId, answer);
      console.log(`[Network] Sent answer to: ${peerId}`);
    } catch (e) {
      console.error(`[Network] Failed to handle offer from ${peerId}:`, e);
    }
  }

  private setupPeerListeners(peer: PeerConnection): void {
    peer.on('connected', ({ peerId }) => {
      console.log(`[Network] Peer connected: ${peerId}`);
      this.emit('peerConnected', { peerId });
    });

    peer.on('disconnected', ({ peerId }) => {
      console.log(`[Network] Peer disconnected: ${peerId}`);
      this.removePeer(peerId);
    });

    peer.on('message', ({ peerId, data }) => {
      this.handleMessage(peerId, data);
    });

    peer.on('error', ({ peerId, error }) => {
      console.error(`[Network] Peer error (${peerId}):`, error);
    });
  }

  private handleMessage(peerId: string, data: ArrayBuffer | string): void {
    try {
      let packet: Packet;

      if (data instanceof ArrayBuffer) {
        packet = this.serializer.deserialize(data);
      } else {
        packet = JSON.parse(data);
      }

      // Handle ping/pong internally
      if (packet.type === PacketType.Pong) {
        const startTime = this.pingTimes.get(peerId);
        if (startTime) {
          const latency = performance.now() - startTime;
          this.latencies.set(peerId, latency);
          this.pingTimes.delete(peerId);
        }
        return;
      }

      if (packet.type === PacketType.Ping) {
        this.sendToPeer(peerId, {
          type: PacketType.Pong,
          timestamp: Date.now(),
          senderId: this.localPeerId,
          data: null,
        });
        return;
      }

      this.emit('packet', { peerId, packet });
    } catch (e) {
      console.error(`[Network] Failed to parse message from ${peerId}:`, e);
    }
  }

  private removePeer(peerId: string): void {
    const peer = this.peers.get(peerId);
    if (peer) {
      peer.close();
      this.peers.delete(peerId);
      this.latencies.delete(peerId);
      this.pingTimes.delete(peerId);
      this.emit('peerDisconnected', { peerId });
    }
  }

  // Public API

  async createRoom(): Promise<void> {
    if (this.useFirestore && this.firestoreSignaling) {
      await this.firestoreSignaling.createRoom();
    } else {
      this.signaling?.createRoom();
    }
  }

  async joinRoom(roomCode: string): Promise<void> {
    if (this.useFirestore && this.firestoreSignaling) {
      await this.firestoreSignaling.joinRoom(roomCode.toUpperCase());
    } else {
      this.signaling?.joinRoom(roomCode.toUpperCase());
    }
  }

  leaveRoom(): void {
    for (const [peerId] of this.peers) {
      this.removePeer(peerId);
    }
    if (this.useFirestore && this.firestoreSignaling) {
      this.firestoreSignaling.leaveRoom();
    } else {
      this.signaling?.leaveRoom();
    }
    this.roomCode = '';
  }

  sendToPeer(peerId: string, packet: Packet): boolean {
    const peer = this.peers.get(peerId);
    if (!peer?.getIsConnected()) return false;

    const data = this.serializer.serialize(packet);
    return peer.send(data);
  }

  broadcast(packet: Packet): void {
    const data = this.serializer.serialize(packet);
    for (const [, peer] of this.peers) {
      if (peer.getIsConnected()) {
        peer.send(data);
      }
    }
  }

  // Send packet as JSON (for debugging or when binary is overkill)
  sendJson(peerId: string, packet: Packet): boolean {
    const peer = this.peers.get(peerId);
    if (!peer?.getIsConnected()) return false;
    return peer.send(JSON.stringify(packet));
  }

  broadcastJson(packet: Packet): void {
    const data = JSON.stringify(packet);
    for (const [, peer] of this.peers) {
      if (peer.getIsConnected()) {
        peer.send(data);
      }
    }
  }

  // Ping all peers
  pingPeers(): void {
    const now = performance.now();
    for (const [peerId, peer] of this.peers) {
      if (peer.getIsConnected()) {
        this.pingTimes.set(peerId, now);
        this.sendToPeer(peerId, {
          type: PacketType.Ping,
          timestamp: Date.now(),
          senderId: this.localPeerId,
          data: null,
        });
      }
    }
  }

  getLatency(peerId: string): number {
    return this.latencies.get(peerId) ?? -1;
  }

  getAverageLatency(): number {
    if (this.latencies.size === 0) return -1;
    let sum = 0;
    for (const latency of this.latencies.values()) {
      sum += latency;
    }
    return sum / this.latencies.size;
  }

  disconnect(): void {
    this.leaveRoom();
    if (this.useFirestore && this.firestoreSignaling) {
      this.firestoreSignaling.disconnect();
      this.firestoreSignaling = null;
    } else {
      this.signaling?.disconnect();
      this.signaling = null;
    }
  }

  // Getters
  getLocalPeerId(): string {
    return this.localPeerId;
  }

  getRoomCode(): string {
    return this.roomCode;
  }

  getPeerIds(): string[] {
    return Array.from(this.peers.keys());
  }

  getPeerCount(): number {
    return this.peers.size;
  }

  getIsHost(): boolean {
    return this.isHost;
  }

  isConnected(): boolean {
    return this.signaling?.isConnected() ?? false;
  }

  // Get the actual RTCPeerConnection for a peer (used by voice chat)
  getPeerConnection(peerId: string): RTCPeerConnection | null {
    const peer = this.peers.get(peerId);
    return peer ? peer.getRTCConnection() : null;
  }
}
